/**
 * SysOpsHome — sysops.mypcb.com home, redesigned 0.15.0 (Bill 2026-11-03) on the shared platform home.
 *
 * Needs attention — only the counts that have a real source (the same reads the old counts strip made):
 *   Kill List to verify        v3.killList.list({ status: 'pending-verification' })
 *   Kill List to execute       v3.killList.list({ status: 'verified' })
 *   Notification failures      v3.notifications.dispatches({ outcome: 'failed', fromUtc: today 00:00 })
 *   Missing operator initials  v3.admin.listMissingOfficerCode()
 * The old strip also showed "Pipeline errors today" and "Stuck awaiting signature", which had no
 * source and always read "—"; they are left out rather than shown as empty numbers. Their pages are
 * still in the menu under Incidents.
 * Recent changes — not shown: SysOps has no change log of its own (the audit pages are in the menu).
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PlatformHome, type AttentionItem, type SearchItem, type StatusTone } from '@retail-ops/panel-shell';
import { asIso8601 } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { ShellLink, useSysopsNav } from '@/components/ShellLayout';

type Count = number | 'error' | null;

interface Counts {
  pendingVerify: Count;
  verifiedNeedsExecute: Count;
  notificationFailures: Count;
  missingOfficerCode: Count;
}

function card(id: string, count: Count, title: string, path: string, busy: { tone: StatusTone; chip: (n: number) => string; text: string }, clear: string): AttentionItem | null {
  if (count === null) return null;
  if (count === 'error') return { id, tone: 'neutral', chip: 'Could not check', title, text: 'The count could not be read. Open the page to look.', path };
  if (count === 0) return { id, tone: 'ok', chip: 'All clear', title, text: clear, path };
  return { id, tone: busy.tone, chip: busy.chip(count), title, text: busy.text, path };
}

export function SysOpsHome(): JSX.Element {
  const api = useApiClient();
  const { tasks, searchItems, devTools } = useSysopsNav();
  const navigate = useNavigate();
  const onPick = useCallback(
    (it: SearchItem) => {
      if (it.external) window.open(it.path, '_blank', 'noopener,noreferrer');
      else void navigate(it.path);
    },
    [navigate],
  );
  const [counts, setCounts] = useState<Counts>({ pendingVerify: null, verifiedNeedsExecute: null, notificationFailures: null, missingOfficerCode: null });

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      try {
        const [pending, verified] = await Promise.all([
          api.retail.killList.list({ status: 'pending-verification', top: 200 }, { signal: ac.signal }),
          api.retail.killList.list({ status: 'verified', top: 200 }, { signal: ac.signal }),
        ]);
        setCounts((c) => ({
          ...c,
          pendingVerify: pending?.count ?? pending?.entries?.length ?? 0,
          verifiedNeedsExecute: verified?.count ?? verified?.entries?.length ?? 0,
        }));
      } catch {
        if (!ac.signal.aborted) setCounts((c) => ({ ...c, pendingVerify: 'error', verifiedNeedsExecute: 'error' }));
      }

      try {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const resp = await api.retail.notifications.dispatches(
          { outcome: 'failed', fromUtc: asIso8601(start.toISOString()), top: 500 },
          { signal: ac.signal },
        );
        setCounts((c) => ({
          ...c,
          notificationFailures: resp?.count ?? resp?.rows?.length ?? (resp as { data?: unknown[] })?.data?.length ?? 0,
        }));
      } catch {
        if (!ac.signal.aborted) setCounts((c) => ({ ...c, notificationFailures: 'error' }));
      }

      try {
        const resp = await api.retail.admin.listMissingOfficerCode({ signal: ac.signal });
        setCounts((c) => ({ ...c, missingOfficerCode: resp?.success ? resp.data.length : 0 }));
      } catch {
        if (!ac.signal.aborted) setCounts((c) => ({ ...c, missingOfficerCode: 'error' }));
      }
    })();
    return () => ac.abort();
  }, [api]);

  const items = [
    card('verify', counts.pendingVerify, 'Kill List: waiting to be verified', '/kill-list', { tone: 'open', chip: (n) => `${n} to verify`, text: 'Cleanup requests waiting to be verified.' }, 'No requests are waiting to be verified.'),
    card('execute', counts.verifiedNeedsExecute, 'Kill List: verified, not yet executed', '/kill-list', { tone: 'open', chip: (n) => `${n} to execute`, text: 'Verified requests waiting to be carried out.' }, 'No verified requests are waiting.'),
    card('notif', counts.notificationFailures, 'Notifications that failed today', '/incidents/notification-failures', { tone: 'problem', chip: (n) => `${n} failed`, text: 'Open the list to retry or escalate.' }, 'No notification has failed today.'),
    card('initials', counts.missingOfficerCode, 'Staff missing operator initials', '/identity/missing-officer-code', { tone: 'watch', chip: (n) => `${n} ${n === 1 ? 'person' : 'people'}`, text: 'Flagged by the server. Resolve each with their Core Director initials.' }, 'Everyone has operator initials.'),
  ].filter((x): x is AttentionItem => !!x);

  const shownTasks = devTools
    ? [
        ...tasks,
        {
          id: 'ofac-rubric',
          title: 'Run the OFAC rubric (developer)',
          blurb: 'Check OFAC screening against a list of test names. Shown because developer tools are on.',
          path: '/data-mgmt/ofac-rubric',
          icon: 'tool' as const,
          crumb: 'Tools › OFAC rubric',
        },
      ]
    : tasks;

  return (
    <PlatformHome
      title="SysOps"
      intro="Operational health for the Retail Operations Platform. Pick a task, or search for what you want to do."
      Link={ShellLink}
      searchItems={searchItems}
      onSearchPick={onPick}
      searchPlaceholder={'What do you want to do?  e.g. "verify a kill", "retry a notification"'}
      attention={{ loading: items.length < 4, items }}
      attentionTour="sysops-counts"
      tasks={shownTasks}
      tasksTour="sysops-tiles"
    />
  );
}
